import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { STATUS_LABELS, STATUS_COLORS, PRIORITY_COLORS, useUser } from '../UserContext';
import CalloutModal from './CalloutModal';

const API = `${window.location.protocol}//${window.location.hostname}/api`;

function api(path, opts = {}) {
  const token = localStorage.getItem('token');
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...opts.headers },
  }).then(async r => {
    const data = await r.json().catch(() => null);
    if (!r.ok) throw new Error(data?.error || 'Request failed');
    return data;
  });
}

// ── Overview sub-components ────────────────────────────────────────────────────

function MetricCard({ label, value, sub, color, onClick }) {
  return (
    <div onClick={onClick}
      style={{ backgroundColor: '#1e293b', borderRadius: 10, padding: '18px 20px', border: `1px solid ${color || '#334155'}22`, borderTop: `3px solid ${color || '#3b82f6'}`, cursor: onClick ? 'pointer' : 'default', transition: 'all 0.15s' }}
      onMouseEnter={e => onClick && (e.currentTarget.style.backgroundColor = '#243050')}
      onMouseLeave={e => onClick && (e.currentTarget.style.backgroundColor = '#1e293b')}
    >
      <div style={{ color: '#64748b', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>{label}</div>
      <div style={{ color: color || '#e2e8f0', fontWeight: 'bold', fontSize: 32, lineHeight: 1 }}>{value}</div>
      {sub !== undefined && <div style={{ color: '#64748b', fontSize: 12, marginTop: 5 }}>{sub}</div>}
    </div>
  );
}

function BarChart({ data, colorFn, labelKey, valueKey, total }) {
  const max = Math.max(...data.map(d => d[valueKey] || 0), 1);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {data.map(d => {
        const val = d[valueKey] || 0;
        const pct = Math.round((val / (total || max)) * 100);
        return (
          <div key={d[labelKey]} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 100, fontSize: 11, color: '#94a3b8', textAlign: 'right', textTransform: 'capitalize', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {d[labelKey]?.replace(/_/g, ' ')}
            </div>
            <div style={{ flex: 1, height: 18, backgroundColor: '#0f172a', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pct}%`, backgroundColor: colorFn(d[labelKey]), borderRadius: 3, display: 'flex', alignItems: 'center', paddingLeft: 6, transition: 'width 0.5s' }}>
                {val > 0 && <span style={{ fontSize: 10, color: 'white', fontWeight: 'bold' }}>{val}</span>}
              </div>
            </div>
            <div style={{ width: 30, fontSize: 11, color: '#64748b' }}>{pct}%</div>
          </div>
        );
      })}
    </div>
  );
}

function OverviewTab({ stats, navigate }) {
  const s = stats.summary;
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 24 }}>
        <MetricCard label="Total Work Items" value={s.total_work_items} color="#3b82f6" onClick={() => navigate('/ops')} />
        <MetricCard label="Open Items" value={s.open_items} sub={`${s.in_progress} in progress`} color="#f59e0b" onClick={() => navigate('/ops')} />
        <MetricCard label="Completion Rate" value={`${s.completion_rate}%`} sub={`${s.closed_items} closed`} color="#22c55e" />
        <MetricCard label="Design Queue" value={s.design_queue} sub="FMPs pending review" color="#ec4899" onClick={() => navigate('/design')} />
        <MetricCard label="Total Sites" value={s.total_locations} color="#8b5cf6" />
        <MetricCard label="FMP Verified" value={`${s.fmp_verification_rate}%`} sub="of generated FMPs" color="#14b8a6" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div style={{ backgroundColor: '#1e293b', borderRadius: 10, padding: 18, border: '1px solid #334155' }}>
          <div style={{ color: '#e2e8f0', fontWeight: 'bold', fontSize: 14, marginBottom: 14 }}>Work Items by Status</div>
          <BarChart data={stats.work_by_status} labelKey="status" valueKey="count" total={s.total_work_items} colorFn={status => STATUS_COLORS[status] || '#64748b'} />
        </div>
        <div style={{ backgroundColor: '#1e293b', borderRadius: 10, padding: 18, border: '1px solid #334155' }}>
          <div style={{ color: '#e2e8f0', fontWeight: 'bold', fontSize: 14, marginBottom: 14 }}>Open Items by Priority</div>
          {stats.work_by_priority.length === 0
            ? <div style={{ color: '#475569', fontSize: 13, textAlign: 'center', padding: 20 }}>No open items 🎉</div>
            : <BarChart data={stats.work_by_priority} labelKey="priority" valueKey="count" total={s.open_items} colorFn={p => PRIORITY_COLORS[p] || '#64748b'} />
          }
        </div>
        <div style={{ backgroundColor: '#1e293b', borderRadius: 10, padding: 18, border: '1px solid #334155' }}>
          <div style={{ color: '#e2e8f0', fontWeight: 'bold', fontSize: 14, marginBottom: 14 }}>Work by Type</div>
          <BarChart data={stats.work_by_type} labelKey="work_type" valueKey="count" total={s.total_work_items} colorFn={() => '#3b82f6'} />
        </div>
        <div style={{ backgroundColor: '#1e293b', borderRadius: 10, padding: 18, border: '1px solid #334155' }}>
          <div style={{ color: '#e2e8f0', fontWeight: 'bold', fontSize: 14, marginBottom: 14 }}>Avg Days to Close</div>
          {stats.avg_close_days.length === 0
            ? <div style={{ color: '#475569', fontSize: 13, textAlign: 'center', padding: 20 }}>No closed items yet</div>
            : stats.avg_close_days.map(d => (
              <div key={d.work_type} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', backgroundColor: '#0f172a', borderRadius: 6, marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: '#94a3b8', textTransform: 'capitalize' }}>{d.work_type?.replace(/_/g, ' ')}</span>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ color: '#e2e8f0', fontWeight: 'bold', fontSize: 16 }}>{d.avg_days}</span>
                  <span style={{ color: '#64748b', fontSize: 11 }}> days avg</span>
                  <div style={{ color: '#475569', fontSize: 10 }}>{d.count} jobs</div>
                </div>
              </div>
            ))
          }
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div style={{ backgroundColor: '#1e293b', borderRadius: 10, padding: 18, border: '1px solid #334155' }}>
          <div style={{ color: '#e2e8f0', fontWeight: 'bold', fontSize: 14, marginBottom: 14 }}>Site Health</div>
          {stats.location_health.length === 0
            ? <div style={{ color: '#475569', fontSize: 13, textAlign: 'center', padding: 20 }}>All sites clear</div>
            : stats.location_health.map(loc => (
              <div key={loc.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 10px', backgroundColor: '#0f172a', borderRadius: 6, borderLeft: `3px solid ${loc.critical_items > 0 ? '#ef4444' : loc.open_items > 0 ? '#f59e0b' : '#22c55e'}`, marginBottom: 6 }}>
                <div>
                  <div style={{ fontSize: 13, color: '#e2e8f0' }}>{loc.name}</div>
                  <div style={{ fontSize: 10, color: '#64748b' }}>{loc.type}</div>
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  {loc.critical_items > 0 && <span style={{ fontSize: 11, fontWeight: 'bold', color: '#ef4444', backgroundColor: '#ef444422', padding: '2px 6px', borderRadius: 4 }}>🔴 {loc.critical_items} critical</span>}
                  {loc.open_items > 0 && <span style={{ fontSize: 11, color: '#f59e0b', backgroundColor: '#f59e0b22', padding: '2px 6px', borderRadius: 4 }}>{loc.open_items} open</span>}
                  {loc.fmps_pending > 0 && <span style={{ fontSize: 11, color: '#ec4899', backgroundColor: '#ec489922', padding: '2px 6px', borderRadius: 4 }}>📄 {loc.fmps_pending} FMP</span>}
                </div>
              </div>
            ))
          }
        </div>
        <div style={{ backgroundColor: '#1e293b', borderRadius: 10, padding: 18, border: '1px solid #334155' }}>
          <div style={{ color: '#e2e8f0', fontWeight: 'bold', fontSize: 14, marginBottom: 14 }}>Team Workload</div>
          {stats.user_workload.length === 0
            ? <div style={{ color: '#475569', fontSize: 13, textAlign: 'center', padding: 20 }}>No assignments yet</div>
            : stats.user_workload.map(u => (
              <div key={u.name} style={{ padding: '8px 10px', backgroundColor: '#0f172a', borderRadius: 6, marginBottom: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                  <div>
                    <span style={{ fontSize: 13, color: '#e2e8f0', fontWeight: 'bold' }}>{u.name}</span>
                    <span style={{ fontSize: 10, color: '#64748b', marginLeft: 6, textTransform: 'capitalize' }}>{u.role?.replace(/_/g, ' ')}</span>
                  </div>
                  <span style={{ fontSize: 12, color: '#f59e0b' }}>{u.active} active</span>
                </div>
                <div style={{ display: 'flex', gap: 6, fontSize: 10, color: '#64748b' }}>
                  <span>{u.assigned} assigned</span><span>·</span>
                  <span style={{ color: '#22c55e' }}>{u.completed} completed</span>
                </div>
              </div>
            ))
          }
        </div>
      </div>

      <div style={{ backgroundColor: '#1e293b', borderRadius: 10, padding: 18, border: '1px solid #334155' }}>
        <div style={{ color: '#e2e8f0', fontWeight: 'bold', fontSize: 14, marginBottom: 14 }}>Recent Activity</div>
        {stats.recent_activity.length === 0
          ? <div style={{ color: '#475569', fontSize: 13, textAlign: 'center', padding: 20 }}>No activity yet</div>
          : stats.recent_activity.map((a, i) => (
            <div key={i} onClick={() => navigate(`/work/${a.work_item_id}`)}
              style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 0', borderBottom: i < stats.recent_activity.length - 1 ? '1px solid #0f172a' : 'none', cursor: 'pointer' }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = '#243050'}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: STATUS_COLORS[a.to_status] || '#475569', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <span style={{ fontSize: 13, color: '#e2e8f0' }}>{a.work_item_title}</span>
                {a.sn_ticket && <span style={{ fontSize: 11, color: '#3b82f6', marginLeft: 6 }}>SN: {a.sn_ticket}</span>}
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 1 }}>
                  → <span style={{ color: STATUS_COLORS[a.to_status] }}>{STATUS_LABELS[a.to_status]}</span>
                  {a.notes && <span style={{ marginLeft: 6 }}>· {a.notes}</span>}
                </div>
              </div>
              <div style={{ fontSize: 11, color: '#475569', flexShrink: 0 }}>
                {a.changed_by_name || 'System'}<br />{new Date(a.changed_at).toLocaleDateString()}
              </div>
            </div>
          ))
        }
      </div>
    </>
  );
}

// ── Dispatch Queue tab ─────────────────────────────────────────────────────────

function SkillBadge({ skill, matched }) {
  return (
    <span style={{
      fontSize: 10, padding: '2px 6px', borderRadius: 4, fontWeight: 700,
      backgroundColor: matched ? '#16603422' : '#1e293b',
      color: matched ? '#4ade80' : '#475569',
      border: `1px solid ${matched ? '#16a34a44' : '#334155'}`,
    }}>{skill}</span>
  );
}

function DispatchQueueTab({ navigate }) {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [assigning, setAssigning] = useState(null); // { item, techId }
  const [statusMsg, setStatusMsg] = useState('');
  const [calloutItem, setCalloutItem] = useState(null); // work item being called out

  const load = useCallback(() => {
    setLoading(true);
    api('/dispatch/queue')
      .then(rows => { setQueue(rows); setLoading(false); })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  useEffect(() => { load(); }, [load]);

  const assign = async (item, techId) => {
    setError('');
    try {
      await api(`/dispatch/assign/${item.id}`, {
        method: 'POST',
        body: JSON.stringify({ tech_id: Number(techId) }),
      });
      const tech = item.suggested_techs.find(t => t.id === Number(techId));
      setStatusMsg(`Dispatched "${item.title}" to ${tech?.name || 'tech'}`);
      setAssigning(null);
      load();
    } catch (err) {
      setError(err.message);
    }
  };

  const priorityColor = { critical: '#ef4444', high: '#f97316' };

  if (loading) return <div style={{ color: '#64748b', padding: 20 }}>Loading dispatch queue…</div>;

  return (
    <div>
      {statusMsg && <div style={{ color: '#22c55e', fontSize: 13, fontWeight: 700, marginBottom: 14 }}>{statusMsg}</div>}
      {error && <div style={{ color: '#f87171', fontSize: 13, fontWeight: 700, marginBottom: 14 }}>{error}</div>}

      {queue.length === 0 ? (
        <div style={{ border: '1px dashed #334155', borderRadius: 10, padding: 48, textAlign: 'center', color: '#64748b' }}>
          <div style={{ fontSize: 28, marginBottom: 10 }}>✅</div>
          <div style={{ fontSize: 15, fontWeight: 700 }}>No Critical or High tickets need assignment.</div>
          <div style={{ fontSize: 13, marginTop: 6 }}>All urgent work is assigned or in progress.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {queue.map(item => {
            const pColor = priorityColor[item.priority] || '#f59e0b';
            const isAssigning = assigning?.item?.id === item.id;
            const requiredSkills = Array.isArray(item.required_skills) ? item.required_skills : [];

            return (
              <div key={item.id} style={{ backgroundColor: '#1e293b', border: `1px solid ${pColor}44`, borderLeft: `4px solid ${pColor}`, borderRadius: 10, padding: 16 }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', padding: '2px 7px', borderRadius: 4, backgroundColor: `${pColor}22`, color: pColor, border: `1px solid ${pColor}44` }}>
                        {item.priority}
                      </span>
                      <span style={{ fontSize: 10, fontWeight: 800, textTransform: 'uppercase', padding: '2px 7px', borderRadius: 4, backgroundColor: '#ef444422', color: '#ef4444', border: '1px solid #ef444444' }}>
                        {item.tech_acceptance_status === 'rejected' ? 'Rejected — Needs Reassignment' : 'Unassigned'}
                      </span>
                    </div>
                    <div style={{ color: '#f8fafc', fontWeight: 800, fontSize: 16 }}>{item.title}</div>
                    <div style={{ color: '#64748b', fontSize: 12, marginTop: 3, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      {item.wo_number && <span>WO {item.wo_number}</span>}
                      {item.sn_ticket && <span>SN {item.sn_ticket}</span>}
                      {item.location_name && <span>📍 {item.location_name}</span>}
                      {item.scheduled_date && <span>📅 {new Date(item.scheduled_date).toLocaleDateString()}</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexDirection: 'column', alignItems: 'flex-end' }}>
                    <button onClick={() => navigate(`/work/${item.id}`)}
                      style={{ padding: '7px 12px', backgroundColor: '#0f172a', color: '#93c5fd', border: '1px solid #1d4ed8', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
                      Open WO
                    </button>
                    <button onClick={() => setCalloutItem(item)}
                      style={{ padding: '7px 12px', backgroundColor: '#450a0a', color: '#ef4444', border: '1px solid #ef4444', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
                      🚨 Callout
                    </button>
                  </div>
                </div>

                {item.tech_reject_reason && (
                  <div style={{ color: '#fca5a5', backgroundColor: '#450a0a', border: '1px solid #7f1d1d', borderRadius: 6, padding: '7px 10px', fontSize: 12, marginBottom: 10 }}>
                    Rejected by {item.prev_tech_name}: "{item.tech_reject_reason}"
                  </div>
                )}

                {requiredSkills.length > 0 && (
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 12 }}>
                    <span style={{ fontSize: 11, color: '#64748b', alignSelf: 'center' }}>Required:</span>
                    {requiredSkills.map(s => <SkillBadge key={s} skill={s} matched={false} />)}
                  </div>
                )}

                {/* Tech suggestions */}
                <div style={{ backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{ padding: '8px 12px', backgroundColor: '#111827', color: '#94a3b8', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Suggested Technicians — skill match · distance
                  </div>
                  {item.suggested_techs.length === 0 ? (
                    <div style={{ padding: '14px 12px', color: '#475569', fontSize: 13 }}>No available techs found.</div>
                  ) : (
                    item.suggested_techs.map((tech, idx) => {
                      const techSkills = Array.isArray(tech.skills) ? tech.skills : [];
                      const matched = requiredSkills.filter(s => techSkills.includes(s));
                      const unmatched = requiredSkills.filter(s => !techSkills.includes(s));
                      const isSelected = isAssigning && assigning.techId === tech.id;

                      return (
                        <div key={tech.id}
                          style={{ padding: '10px 12px', borderTop: idx > 0 ? '1px solid #1e293b' : 'none', display: 'flex', alignItems: 'center', gap: 12, backgroundColor: isSelected ? '#1e3a5f' : 'transparent' }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
                              <span style={{ color: '#f8fafc', fontWeight: 700, fontSize: 13 }}>{tech.name}</span>
                              {tech.career_level && <span style={{ fontSize: 10, color: '#64748b' }}>{tech.career_level}</span>}
                              {tech.active_jobs > 0 && (
                                <span style={{ fontSize: 10, color: '#f59e0b', backgroundColor: '#f59e0b22', padding: '1px 5px', borderRadius: 3 }}>
                                  {tech.active_jobs} active job{tech.active_jobs !== 1 ? 's' : ''}
                                </span>
                              )}
                            </div>
                            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                              {tech.distance_miles !== null && tech.distance_miles !== undefined && (
                                <span style={{ fontSize: 11, color: '#3b82f6' }}>📍 {tech.distance_miles} mi</span>
                              )}
                              {tech.reporting_location && (
                                <span style={{ fontSize: 11, color: '#64748b' }}>from {tech.reporting_location}</span>
                              )}
                              {tech.shift_start && (
                                <span style={{ fontSize: 11, color: '#64748b' }}>
                                  shift {String(tech.shift_start).slice(0, 5)}–{String(tech.shift_end).slice(0, 5)}
                                </span>
                              )}
                            </div>
                            {requiredSkills.length > 0 && (
                              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 5 }}>
                                {matched.map(s => <SkillBadge key={s} skill={s} matched />)}
                                {unmatched.map(s => <SkillBadge key={s} skill={s} matched={false} />)}
                                {matched.length === 0 && requiredSkills.length > 0 && (
                                  <span style={{ fontSize: 10, color: '#ef4444' }}>No skill match</span>
                                )}
                              </div>
                            )}
                          </div>
                          <button
                            onClick={() => assign(item, tech.id)}
                            style={{ padding: '7px 14px', backgroundColor: '#166534', color: '#dcfce7', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 800, fontSize: 12, whiteSpace: 'nowrap' }}>
                            Dispatch
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {calloutItem && (
        <CalloutModal
          workItemId={calloutItem.id}
          workOrderNumber={calloutItem.wo_number || calloutItem.work_order_number}
          priority={calloutItem.priority}
          title={calloutItem.title}
          onClose={() => setCalloutItem(null)}
        />
      )}
    </div>
  );
}

// ── SLA Settings tab ───────────────────────────────────────────────────────────

const PRIORITY_LABELS = { 1: 'Critical', 2: 'High', 3: 'Medium', 4: 'Low' };
const PRIORITY_COLORS_MAP = { 1: '#ef4444', 2: '#f97316', 3: '#f59e0b', 4: '#22c55e' };

function SlaSettingsTab() {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState('');
  const [statusMsg, setStatusMsg] = useState('');
  const [edits, setEdits] = useState({});

  useEffect(() => {
    api('/dispatch/sla-rules')
      .then(rows => {
        setRules(rows);
        const init = {};
        rows.forEach(r => {
          init[r.priority] = {
            cutoff_time: r.cutoff_time ? String(r.cutoff_time).slice(0, 5) : '',
            queue_distribution_time: r.queue_distribution_time ? String(r.queue_distribution_time).slice(0, 5) : '',
            always_dispatch: r.always_dispatch,
          };
        });
        setEdits(init);
        setLoading(false);
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, []);

  const save = async (priority) => {
    setSaving(priority);
    setError('');
    setStatusMsg('');
    try {
      const updated = await api(`/dispatch/sla-rules/${priority}`, {
        method: 'PATCH',
        body: JSON.stringify({
          cutoff_time: edits[priority].cutoff_time || null,
          queue_distribution_time: edits[priority].queue_distribution_time || null,
          always_dispatch: edits[priority].always_dispatch,
        }),
      });
      setRules(prev => prev.map(r => r.priority === priority ? updated : r));
      setStatusMsg(`${PRIORITY_LABELS[priority]} SLA saved`);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(null);
    }
  };

  const inputStyle = {
    padding: '7px 10px', backgroundColor: '#0f172a', border: '1px solid #334155',
    borderRadius: 6, color: '#e2e8f0', fontSize: 13, width: '100%', boxSizing: 'border-box',
  };

  if (loading) return <div style={{ color: '#64748b', padding: 20 }}>Loading SLA rules…</div>;

  return (
    <div style={{ maxWidth: 800 }}>
      <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 20 }}>
        Configure when tickets stop dropping into tech work queues and must be manually dispatched by a manager.
        Critical tickets always route to the dispatch queue. For other priorities, set a cutoff time — tickets submitted
        after that time hold until the next queue distribution.
      </div>

      {(error || statusMsg) && (
        <div style={{ color: error ? '#f87171' : '#22c55e', fontSize: 13, fontWeight: 700, marginBottom: 14 }}>
          {error || statusMsg}
        </div>
      )}

      <div style={{ border: '1px solid #1e293b', borderRadius: 10, overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr 1fr 140px 80px', gap: 0, backgroundColor: '#111827', padding: '10px 16px', borderBottom: '1px solid #1e293b' }}>
          {['Priority', 'Cutoff Time', 'Queue Distribution', 'Always Dispatch', ''].map(h => (
            <div key={h} style={{ color: '#64748b', fontSize: 11, fontWeight: 800, textTransform: 'uppercase' }}>{h}</div>
          ))}
        </div>

        {rules.map((rule, idx) => {
          const edit = edits[rule.priority] || {};
          const color = PRIORITY_COLORS_MAP[rule.priority] || '#64748b';

          return (
            <div key={rule.priority}
              style={{ display: 'grid', gridTemplateColumns: '130px 1fr 1fr 140px 80px', gap: 0, padding: '14px 16px', alignItems: 'center', borderBottom: idx < rules.length - 1 ? '1px solid #111827' : 'none', backgroundColor: '#1e293b' }}>

              {/* Priority label */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: color }} />
                <span style={{ color: '#f8fafc', fontWeight: 800, fontSize: 14 }}>{PRIORITY_LABELS[rule.priority]}</span>
              </div>

              {/* Cutoff time */}
              <div style={{ paddingRight: 12 }}>
                {edit.always_dispatch ? (
                  <span style={{ color: '#475569', fontSize: 12 }}>— Always dispatches immediately</span>
                ) : (
                  <div>
                    <div style={{ color: '#94a3b8', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Stop Time</div>
                    <input
                      type="time"
                      value={edit.cutoff_time || ''}
                      onChange={e => setEdits(prev => ({ ...prev, [rule.priority]: { ...prev[rule.priority], cutoff_time: e.target.value } }))}
                      style={inputStyle}
                    />
                    <div style={{ color: '#475569', fontSize: 10, marginTop: 3 }}>Tickets submitted after this time wait for next queue run</div>
                  </div>
                )}
              </div>

              {/* Queue distribution time */}
              <div style={{ paddingRight: 12 }}>
                <div style={{ color: '#94a3b8', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>Queue Distribution</div>
                <input
                  type="time"
                  value={edit.queue_distribution_time || ''}
                  onChange={e => setEdits(prev => ({ ...prev, [rule.priority]: { ...prev[rule.priority], queue_distribution_time: e.target.value } }))}
                  style={inputStyle}
                />
                <div style={{ color: '#475569', fontSize: 10, marginTop: 3 }}>Time tickets distribute to tech queues each day</div>
              </div>

              {/* Always dispatch toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <div
                    onClick={() => setEdits(prev => ({ ...prev, [rule.priority]: { ...prev[rule.priority], always_dispatch: !edit.always_dispatch } }))}
                    style={{
                      width: 40, height: 22, borderRadius: 11, cursor: 'pointer', transition: 'background 0.2s',
                      backgroundColor: edit.always_dispatch ? '#2563eb' : '#334155',
                      position: 'relative', flexShrink: 0,
                    }}
                  >
                    <div style={{
                      width: 16, height: 16, borderRadius: '50%', backgroundColor: 'white',
                      position: 'absolute', top: 3,
                      left: edit.always_dispatch ? 21 : 3,
                      transition: 'left 0.2s',
                    }} />
                  </div>
                  <span style={{ color: edit.always_dispatch ? '#93c5fd' : '#64748b', fontSize: 12, fontWeight: 700 }}>
                    {edit.always_dispatch ? 'On' : 'Off'}
                  </span>
                </label>
              </div>

              {/* Save button */}
              <div>
                <button
                  onClick={() => save(rule.priority)}
                  disabled={saving === rule.priority}
                  style={{ padding: '7px 14px', backgroundColor: saving === rule.priority ? '#1e3a5f' : '#2563eb', color: 'white', border: 'none', borderRadius: 6, cursor: saving === rule.priority ? 'not-allowed' : 'pointer', fontWeight: 800, fontSize: 12 }}>
                  {saving === rule.priority ? '…' : 'Save'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 16, padding: '12px 14px', backgroundColor: '#111827', border: '1px solid #1e293b', borderRadius: 8, fontSize: 12, color: '#64748b' }}>
        <strong style={{ color: '#94a3b8' }}>How SLA routing works:</strong>
        <ul style={{ margin: '6px 0 0 16px', padding: 0, lineHeight: 1.7 }}>
          <li><strong style={{ color: '#ef4444' }}>Critical</strong> — Always dispatches immediately to manager queue regardless of time.</li>
          <li><strong style={{ color: '#f97316' }}>High</strong> — Routes to dispatch queue for manual assignment before cutoff. After cutoff, holds until next queue run.</li>
          <li><strong style={{ color: '#f59e0b' }}>Medium / Low</strong> — Drops directly into tech work queues at queue distribution time.</li>
        </ul>
      </div>
    </div>
  );
}

// ── Executive Dashboard tab ────────────────────────────────────────────────────

function ExecDashTab() {
  const [jhas, setJhas] = useState([]);
  const [fmpSummary, setFmpSummary] = useState([]);
  const [opcos, setOpcos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api('/jhas').catch(() => []),
      api('/fmp-verifications/summary').catch(() => []),
      api('/orgs/opcos').catch(() => []),
    ]).then(([jhaRows, fmpRows, opcoRows]) => {
      setJhas(Array.isArray(jhaRows) ? jhaRows : []);
      setFmpSummary(Array.isArray(fmpRows) ? fmpRows : []);
      setOpcos(Array.isArray(opcoRows) ? opcoRows : []);
      setLoading(false);
    });
  }, []);

  if (loading) return <div style={{ color: '#64748b', padding: 20 }}>Loading…</div>;

  // JHA compliance
  const submitted = jhas.filter(j => j.jha_submitted_at).length;
  const avgLag = jhas.filter(j => j.jha_lag_minutes != null);
  const avgLagVal = avgLag.length ? Math.round(avgLag.reduce((s, j) => s + j.jha_lag_minutes, 0) / avgLag.length) : null;
  const postDone = jhas.filter(j => j.status === 'post_job_complete').length;

  // FMP
  const totalChecks = fmpSummary.reduce((s, r) => s + r.total_checks, 0);
  const totalDiscrep = fmpSummary.reduce((s, r) => s + r.discrepancies, 0);

  const statCard = (label, value, color, sub) => (
    <div style={{ backgroundColor: '#1e293b', border: `1px solid ${color}22`, borderTop: `3px solid ${color}`, borderRadius: 10, padding: '14px 16px' }}>
      <div style={{ color: '#64748b', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', marginBottom: 4 }}>{label}</div>
      <div style={{ color, fontSize: 26, fontWeight: 800 }}>{value ?? '—'}</div>
      {sub && <div style={{ color: '#64748b', fontSize: 11, marginTop: 3 }}>{sub}</div>}
    </div>
  );

  return (
    <div>
      <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 18 }}>Executive view — JHA compliance, FMP spot checks, and org summary.</div>

      <div style={{ marginBottom: 20 }}>
        <div style={{ color: '#e2e8f0', fontWeight: 800, fontSize: 14, marginBottom: 12 }}>JHA Compliance</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 16 }}>
          {statCard('Total JHAs', jhas.length, '#3b82f6')}
          {statCard('Pre-Job Submitted', submitted, '#22c55e', `${jhas.length ? Math.round(submitted / jhas.length * 100) : 0}% submission rate`)}
          {statCard('Avg Lag (min)', avgLagVal, avgLagVal != null && avgLagVal > 30 ? '#ef4444' : '#22c55e', 'site arrival → submission')}
          {statCard('Post-Job Complete', postDone, '#8b5cf6', `${submitted ? Math.round(postDone / submitted * 100) : 0}% of submitted`)}
        </div>

        {/* Per-tech JHA table */}
        {jhas.length > 0 && (() => {
          const byTech = {};
          jhas.forEach(j => {
            const k = j.conducted_by_name || `User-${j.conducted_by}`;
            if (!byTech[k]) byTech[k] = { name: k, total: 0, submitted: 0, lags: [] };
            byTech[k].total++;
            if (j.jha_submitted_at) byTech[k].submitted++;
            if (j.jha_lag_minutes != null) byTech[k].lags.push(j.jha_lag_minutes);
          });
          const rows = Object.values(byTech).sort((a, b) => b.total - a.total);
          return (
            <div style={{ border: '1px solid #1e293b', borderRadius: 8, overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ backgroundColor: '#111827' }}>
                    {['Tech', 'JHAs', 'Submitted', 'Avg Lag (min)'].map(h => (
                      <th key={h} style={{ padding: '9px 12px', textAlign: 'left', color: '#64748b', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', borderBottom: '1px solid #1e293b' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map(r => {
                    const lag = r.lags.length ? Math.round(r.lags.reduce((s, v) => s + v, 0) / r.lags.length) : null;
                    const lagColor = lag == null ? '#64748b' : lag <= 15 ? '#22c55e' : lag <= 30 ? '#f59e0b' : '#ef4444';
                    return (
                      <tr key={r.name} style={{ borderBottom: '1px solid #111827' }}>
                        <td style={{ padding: '9px 12px', color: '#e2e8f0', fontWeight: 700 }}>{r.name}</td>
                        <td style={{ padding: '9px 12px', color: '#94a3b8' }}>{r.total}</td>
                        <td style={{ padding: '9px 12px', color: '#22c55e' }}>{r.submitted}</td>
                        <td style={{ padding: '9px 12px', color: lagColor, fontWeight: 700 }}>{lag ?? '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })()}
      </div>

      <div style={{ marginBottom: 20 }}>
        <div style={{ color: '#e2e8f0', fontWeight: 800, fontSize: 14, marginBottom: 12 }}>FMP Spot-Check Summary</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 16 }}>
          {statCard('Total Checks', totalChecks, '#14b8a6')}
          {statCard('Discrepancies', totalDiscrep, totalDiscrep > 0 ? '#ef4444' : '#22c55e', `${totalChecks ? Math.round((1 - totalDiscrep / totalChecks) * 100) : 100}% pass rate`)}
        </div>
        {fmpSummary.length > 0 && (
          <div style={{ border: '1px solid #1e293b', borderRadius: 8, overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ backgroundColor: '#111827' }}>
                  {['Tech', 'Checks', 'Passed', 'Discrepancies'].map(h => (
                    <th key={h} style={{ padding: '9px 12px', textAlign: 'left', color: '#64748b', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', borderBottom: '1px solid #1e293b' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {fmpSummary.map(r => (
                  <tr key={r.tech_id} style={{ borderBottom: '1px solid #111827' }}>
                    <td style={{ padding: '9px 12px', color: '#e2e8f0', fontWeight: 700 }}>{r.tech_name}</td>
                    <td style={{ padding: '9px 12px', color: '#94a3b8' }}>{r.total_checks}</td>
                    <td style={{ padding: '9px 12px', color: '#22c55e' }}>{r.passed}</td>
                    <td style={{ padding: '9px 12px', color: r.discrepancies > 0 ? '#ef4444' : '#22c55e', fontWeight: 800 }}>{r.discrepancies}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div>
        <div style={{ color: '#e2e8f0', fontWeight: 800, fontSize: 14, marginBottom: 12 }}>Org Breakdown</div>
        {opcos.length === 0 ? (
          <div style={{ color: '#475569', fontSize: 13, textAlign: 'center', padding: 24, border: '1px dashed #334155', borderRadius: 8 }}>No OPCOs configured yet.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
            {opcos.map(o => (
              <div key={o.id} style={{ backgroundColor: '#1e293b', border: '1px solid #1e293b', borderRadius: 10, padding: '14px 16px' }}>
                <div style={{ color: '#f8fafc', fontWeight: 800, fontSize: 14 }}>{o.name}</div>
                {o.director_name && <div style={{ color: '#64748b', fontSize: 12, marginTop: 2 }}>Dir: {o.director_name}</div>}
                <div style={{ display: 'flex', gap: 14, marginTop: 10 }}>
                  <div><div style={{ color: '#64748b', fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}>Teams</div><div style={{ color: '#e2e8f0', fontWeight: 800, fontSize: 18 }}>{o.team_count}</div></div>
                  <div><div style={{ color: '#64748b', fontSize: 10, fontWeight: 700, textTransform: 'uppercase' }}>Members</div><div style={{ color: '#e2e8f0', fontWeight: 800, fontSize: 18 }}>{o.member_count}</div></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Dashboard shell ────────────────────────────────────────────────────────────

const TABS = [
  { key: 'overview',  label: 'Overview' },
  { key: 'dispatch',  label: 'Dispatch Queue' },
  { key: 'sla',       label: 'SLA Settings' },
  { key: 'executive', label: 'Executive' },
];

export default function Dashboard() {
  const user = useUser();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const navigate = useNavigate();

  const isManager = ['admin', 'rpm', 'management', 'managing_director', 'director', 'field_manager'].includes(user?.role);
  const isExec = ['admin', 'managing_director', 'director'].includes(user?.role);

  useEffect(() => {
    api('/dashboard/stats')
      .then(data => { setStats(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#0f172a', color: '#64748b', fontFamily: 'sans-serif' }}>
      Loading dashboard...
    </div>
  );

  const visibleTabs = TABS.filter(t => {
    if (t.key === 'executive') return isExec;
    return isManager || t.key === 'overview';
  });

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', backgroundColor: '#0f172a', fontFamily: 'sans-serif', overflow: 'hidden' }}>
      {/* Tab bar */}
      <div style={{ borderBottom: '1px solid #1e293b', padding: '0 20px', display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
        {visibleTabs.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} style={{
            padding: '13px 16px', border: 'none',
            borderBottom: tab === t.key ? '2px solid #3b82f6' : '2px solid transparent',
            backgroundColor: 'transparent',
            color: tab === t.key ? '#93c5fd' : '#64748b',
            cursor: 'pointer', fontSize: 13,
            fontWeight: tab === t.key ? 800 : 600,
          }}>{t.label}</button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        <div style={{ maxWidth: tab === 'overview' ? 1200 : 960, margin: '0 auto' }}>
          {tab === 'overview' && stats && <OverviewTab stats={stats} navigate={navigate} />}
          {tab === 'dispatch'  && <DispatchQueueTab navigate={navigate} />}
          {tab === 'sla'       && <SlaSettingsTab />}
          {tab === 'executive' && <ExecDashTab />}
        </div>
      </div>
    </div>
  );
}
